import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './NoticiaDetalle.css';

const NoticiaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [noticia, setNoticia] = useState(null);
  const [noticiasRelacionadas, setNoticiasRelacionadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchNoticia = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/noticias/${id}/`);
        setNoticia(response.data);
        
        const todasResponse = await axios.get(`${process.env.REACT_APP_API_URL}/noticias/?page_size=10`);
        const noticiasData = todasResponse.data.results || todasResponse.data;
        const relacionadas = noticiasData.filter(n => n.id !== parseInt(id)).slice(0, 3);
        setNoticiasRelacionadas(relacionadas);
        
        setError(null);
      } catch (err) {
        console.error('Error cargando noticia:', err);
        setError('Error al cargar la noticia');
      } finally {
        setLoading(false);
      }
    };

    fetchNoticia();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const formatFecha = (fechaString) => {
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-AR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const openModal = (image) => {
    setSelectedImage(image);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [modalOpen]);

  if (loading) {
    return (
      <section className="noticia-detalle-section">
        <div className="noticia-detalle-container">
          <div className="noticia-loading">
            <p>Cargando noticia...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !noticia) {
    return (
      <section className="noticia-detalle-section">
        <div className="noticia-detalle-container">
          <div className="noticia-error">
            <p>{error || 'Noticia no encontrada'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="noticia-detalle-section">
      <div className="noticia-detalle-container">
        <div className="noticia-detalle">
          <h2 className="noticia-detalle-title">{noticia.titulo}</h2>       
          {noticia.imagen_principal_url && (
            <div className="noticia-detalle-imagen-principal">
              <img 
                src={noticia.imagen_principal_url} 
                alt={noticia.titulo} 
                loading="eager"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <p className="noticia-detalle-fecha">{formatFecha(noticia.fecha_publicacion)}</p>
          <div 
            className="noticia-detalle-contenido"
            dangerouslySetInnerHTML={{ __html: noticia.contenido }}
          />
          
          {noticia.imagenes_extra_data && noticia.imagenes_extra_data.length > 0 && (
            <div className="noticia-galeria">
              <h3 className="noticia-galeria-titulo">Galería de imágenes</h3>
              <div className="noticia-galeria-grid">
                {noticia.imagenes_extra_data.map((img, index) => (
                  <div key={index} className="noticia-galeria-item">
                    <img 
                      src={img.thumbnail || img.imagen} 
                      alt={img.descripcion || `Imagen ${index + 1}`}
                      loading="lazy"
                      onClick={() => openModal(img)}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {noticiasRelacionadas.length > 0 && (
          <div className="noticias-relacionadas">
            <h3 className="noticias-relacionadas-titulo">Notas relacionadas</h3>
            <div className="noticias-relacionadas-grid">
              {noticiasRelacionadas.map((rel) => (
                <div key={rel.id} className="noticia-relacionada-card" onClick={() => navigate(`/novedades/${rel.id}`)}>
                  <div className="noticia-relacionada-imagen">
                    <img 
                      src={rel.imagen_principal_thumbnail || rel.imagen_principal_url || '/placeholder-image.jpg'} 
                      alt={rel.titulo}
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </div>
                  <div className="noticia-relacionada-contenido">
                    <h4 className="noticia-relacionada-titulo">{rel.titulo}</h4>
                    <p className="noticia-relacionada-fecha">{formatFecha(rel.fecha_publicacion)}</p>
                    <p className="noticia-relacionada-descripcion">
                      {(() => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = rel.contenido;
                        const textoLimpio = tempDiv.textContent || tempDiv.innerText || '';
                        
                        return textoLimpio.length > 100 
                          ? `${textoLimpio.substring(0, 100)}...` 
                          : textoLimpio;
                      })()}
                    </p>
                    <button className="ver-noticia-btn">
                      Ver noticia
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modalOpen && selectedImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.imagen} alt={selectedImage.descripcion || "Imagen ampliada"} />
            <button className="modal-close-btn" onClick={closeModal}>×</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default NoticiaDetalle; 
